import { useContext, useEffect } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { MattersActions } from '@/matter/store/slice'
import { RootState } from '@/store/store'
import { AuthContext } from '@/context/auth-context'
import { kSessionStorageActiveMatterIdKey } from '../constants'
import { SetActiveMatterSource } from '@/matter/enums/set-active-matter-source.enums'

/**
 * Restores the active matter id from sessionStorage on initial app load.
 *
 * This hook runs once when the matters list loads for the first time and restores
 * the previously selected matter from sessionStorage. This ensures matter context
 * persists across page refreshes.
 *
 * Important: This hook does NOT clear the active matter when navigating to non-matter
 * routes. Matter context is application-level state that persists until explicitly
 * changed by user action (selecting a different matter or deleting the active matter).
 */
export function useRestoreSessionMatter() {
  const dispatch = useAppDispatch()
  const authContext = useContext(AuthContext)
  const { user } = authContext
  const userIsAnon = user?.isAnonymous

  // Redux state
  const initialLoadComplete = useAppSelector((state: RootState) => state.mattersState.initialLoadComplete)
  const matterRecords = useAppSelector((state: RootState) => state.mattersState.matters)
  const activeMatterId = useAppSelector((state: RootState) => state.mattersState.activeMatterId)

  /**
   * Restore matter context from sessionStorage on initial load only.
   *
   * This effect runs when the matters list finishes loading. If there's no active
   * matter in Redux but there is one in sessionStorage, we restore it.
   */
  useEffect(() => {
    // Skip for anonymous users - they don't have matter context
    if (userIsAnon) return

    // Wait for matters to load before attempting restore
    if (!initialLoadComplete) return

    // If there's already an active matter, don't override it
    // This is the key change: we respect the existing Redux state
    if (activeMatterId) return

    // Attempt to restore from sessionStorage
    const storedMatterId = sessionStorage.getItem(kSessionStorageActiveMatterIdKey)
    if (!storedMatterId) return

    // Validate the stored matter still exists in the user's matter list
    if (storedMatterId in matterRecords) {
      dispatch(
        MattersActions.setActiveMatterId({
          matterId: storedMatterId,
          source: SetActiveMatterSource.SessionRestore,
        })
      )
    } else {
      // Only clear if the matter no longer exists (was deleted)
      sessionStorage.removeItem(kSessionStorageActiveMatterIdKey)
    }
  }, [initialLoadComplete, matterRecords, userIsAnon, activeMatterId, dispatch])
}
